/**
 * Actions of the Ausschreibungs-Board: the labelled quick actions on a row, the dialogs they open,
 * and the batch bar for a selection.
 *
 * A row action and a batch action are the same thing applied to one row or to several — so both go
 * through one `BoardActionDialog` and the board's `runOnRows`, which calls the existing per-tender
 * endpoints. There is no batch endpoint on the server: owner, handover, dismiss and start are
 * per-tender writes with their own checks and their own trail entry, and repeating that loop on the
 * server would add a second place for those rules without saving a single check.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Loader2, MoreHorizontal, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { Dropdown } from "@/components/fluent-dropdown-compat";
import {
  TenderBoardRow,
  TenderExistingCustomerCheck,
  useTenderSearchProfileAPI,
} from "../../TenderSearchProfileAPI";

export interface Option {
  key: string;
  text: string;
}

/** What a dialog is open for, and on which rows. */
export type BoardDialog =
  | { kind: "dismiss"; rows: TenderBoardRow[] }
  | { kind: "handover"; rows: TenderBoardRow[] }
  | { kind: "assign"; rows: TenderBoardRow[] }
  | { kind: "startProcess"; rows: TenderBoardRow[] }
  | { kind: "existingCustomer"; rows: TenderBoardRow[] };

/** A triage row — the only kind a process can be started on or that can be dismissed. */
export const isTriage = (row: TenderBoardRow) => !row.process && row.triageState !== "dismissed";

/**
 * The labelled actions of one row. Shown on hover and on keyboard focus; the "Mehr" trigger stays
 * visible, so the row always says that it has actions. Clicks stop here: the row itself expands on
 * click, and an action must not also open the detail.
 */
export function RowActions({
  row,
  currentUser,
  busy,
  canStart,
  onTake,
  onRelease,
  onRestore,
  onOpen,
}: {
  row: TenderBoardRow;
  currentUser: string | null;
  busy: boolean;
  /** A workflow exists that a process could be started on. */
  canStart: boolean;
  onTake: () => void;
  onRelease: () => void;
  onRestore: () => void;
  onOpen: (dialog: BoardDialog) => void;
}) {
  const { t } = useTranslation();
  const mine = !!currentUser && row.ownerUserId === currentUser;
  // Dismissed with or without a process: a running bid can be dismissed too (from „Mehr“).
  const dismissed = row.triageState === "dismissed";

  return (
    // The cell is narrow; the buttons overlay the cells to their left while the row is hovered, so
    // the columns keep their width when nothing is hovered.
    <div className="relative flex h-full items-center justify-end" onClick={event => event.stopPropagation()}>
      <div
        className={
          "absolute right-10 top-1/2 z-10 flex -translate-y-1/2 items-center gap-1 rounded-md bg-background/95 pl-2 " +
          "pointer-events-none opacity-0 transition-opacity " +
          "[tr:hover_&]:pointer-events-auto [tr:hover_&]:opacity-100 " +
          "[tr:focus-within_&]:pointer-events-auto [tr:focus-within_&]:opacity-100"
        }
      >
        {busy && <Loader2 size={14} className="animate-spin text-muted-foreground" />}
        {mine ? (
          <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={onRelease}>
            {t("tenderBoard.actions.release")}
          </Button>
        ) : (
          <Button size="sm" className="h-8" disabled={busy || !currentUser} onClick={onTake}>
            {t("tenderBoard.actions.take")}
          </Button>
        )}
        {isTriage(row) && (
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            disabled={busy || !canStart}
            title={canStart ? undefined : t("tenderBoard.actions.noWorkflows")}
            onClick={() => onOpen({ kind: "startProcess", rows: [row] })}
          >
            {t("tenderBoard.actions.startProcess")}
          </Button>
        )}
        {isTriage(row) && (
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            disabled={busy}
            onClick={() => onOpen({ kind: "dismiss", rows: [row] })}
          >
            {t("tenderBoard.actions.dismiss")}
          </Button>
        )}
        {dismissed && (
          <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={onRestore}>
            {t("tenderBoard.actions.restore")}
          </Button>
        )}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 gap-1 px-2"
            aria-label={t("tenderBoard.actions.more")}
            title={t("tenderBoard.actions.more")}
          >
            <MoreHorizontal size={16} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuItem onSelect={() => onOpen({ kind: "existingCustomer", rows: [row] })}>
            <MenuEntry
              title={t("tenderBoard.actions.existingCustomer")}
              hint={t("tenderBoard.actions.existingCustomerHint")}
            />
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onOpen({ kind: "handover", rows: [row] })}>
            <MenuEntry title={t("tenderBoard.actions.handoverTo")} hint={t("tenderBoard.actions.handoverHint")} />
          </DropdownMenuItem>
          {!dismissed && (
            <>
              <DropdownMenuSeparator />
              {/*
                Der Wunsch „Ausschreibung abbrechen“ (Geiger FM) ist fachlich Verwerfen (Entscheidung
                19.10.2026) und heißt deshalb auch so: derselbe Dialog und Endpunkt, Grund optional,
                hier auch für Ausschreibungen in Bearbeitung. Die Engine wird nicht angefasst — ein
                laufender Prozess läuft weiter, und der Dialog sagt das.
              */}
              <DropdownMenuItem onSelect={() => onOpen({ kind: "dismiss", rows: [row] })}>
                <MenuEntry title={t("tenderBoard.actions.dismissTender")} hint={t("tenderBoard.actions.dismissTenderHint")} />
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function MenuEntry({ title, hint }: { title: string; hint: string }) {
  return (
    <span className="flex flex-col">
      <span>{title}</span>
      <span className="text-xs text-muted-foreground">{hint}</span>
    </span>
  );
}

/**
 * The bar at the bottom while rows are selected. Fixed to the viewport, so it stays in reach while
 * the list scrolls; it names how many selected rows have no documents, because that is the one
 * thing a batch "Übernehmen" would otherwise hide.
 */
export function BatchBar({
  rows,
  busy,
  onTake,
  onOpen,
  onClear,
}: {
  rows: TenderBoardRow[];
  busy: boolean;
  onTake: () => void;
  onOpen: (dialog: BoardDialog) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  if (rows.length === 0) return null;
  const withoutDocuments = rows.filter(row => row.documents?.count === 0).length;
  // Every row not yet dismissed, running bids included — the dialog says that their process keeps
  // running.
  const dismissable = rows.filter(row => row.triageState !== "dismissed");

  return (
    <div
      role="region"
      aria-label={t("tenderBoard.batch.region")}
      className="fixed inset-x-4 bottom-4 z-40 mx-auto flex max-w-[1400px] flex-wrap items-center gap-2 rounded-lg bg-foreground px-4 py-3 text-background shadow-lg"
    >
      <span className="mr-2 text-sm font-semibold">{t("tenderBoard.batch.selected", { count: rows.length })}</span>
      <Button size="sm" disabled={busy} onClick={onTake}>
        {t("tenderBoard.actions.take")}
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="border-background/40 bg-transparent text-background hover:bg-background/10 hover:text-background"
        disabled={busy}
        onClick={() => onOpen({ kind: "assign", rows })}
      >
        {t("tenderBoard.batch.assign")}
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="border-background/40 bg-transparent text-background hover:bg-background/10 hover:text-background"
        disabled={busy || dismissable.length === 0}
        title={dismissable.length === 0 ? t("tenderBoard.batch.nothingToDismiss") : undefined}
        onClick={() => onOpen({ kind: "dismiss", rows: dismissable })}
      >
        {t("tenderBoard.batch.dismiss")}
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="border-background/40 bg-transparent text-background hover:bg-background/10 hover:text-background"
        disabled={busy}
        onClick={() => onOpen({ kind: "existingCustomer", rows })}
      >
        {t("tenderBoard.batch.existingCustomer")}
      </Button>
      {busy && <Loader2 size={16} className="animate-spin" />}
      <span className="ml-auto text-xs opacity-80">
        {withoutDocuments > 0 && t("tenderBoard.batch.withoutDocuments", { count: withoutDocuments })}
      </span>
      <Button
        size="sm"
        variant="ghost"
        className="gap-1 text-background hover:bg-background/10 hover:text-background"
        onClick={onClear}
      >
        <X size={14} /> {t("tenderBoard.batch.clear")}
      </Button>
    </div>
  );
}

/**
 * One dialog for every action that needs an input first — a reason, a department, a person, a
 * workflow — or that shows a result (the Bestandskunden-Prüfung). Works the same for one row and for
 * a selection; `onRun` receives the input and the board applies it row by row.
 */
export function BoardActionDialog({
  dialog,
  roles,
  people,
  definitions,
  busy,
  onClose,
  onRun,
}: {
  dialog: BoardDialog | null;
  roles: Option[];
  /** Who a tender can be assigned to: e-mail → display name. */
  people: Option[];
  definitions: Option[];
  busy: boolean;
  onClose: () => void;
  onRun: (dialog: BoardDialog, value: string) => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");

  // A fresh input per opening; with a single workflow there is nothing to choose.
  useEffect(() => {
    setValue(dialog?.kind === "startProcess" && definitions.length === 1 ? definitions[0].key : "");
  }, [dialog, definitions]);

  if (!dialog) return null;
  const count = dialog.rows.length;
  const single = count === 1 ? dialog.rows[0].title : null;
  const subject = single ?? t("tenderBoard.batch.selected", { count });

  const confirm = (label: string, enabled: boolean) => (
    <DialogFooter className="gap-2">
      <Button variant="outline" onClick={onClose}>
        {/* „Schließen“, not „Abbrechen“: next to „Verwerfen“ it would read like a second way to drop it. */}
        {dialog.kind === "dismiss" ? t("global.close") : t("global.cancel")}
      </Button>
      <Button disabled={busy || !enabled} className="gap-2" onClick={() => onRun(dialog, value)}>
        {busy && <Loader2 size={14} className="animate-spin" />}
        {label}
      </Button>
    </DialogFooter>
  );

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-[560px]">
        {dialog.kind === "dismiss" && (
          <>
            <DialogHeader>
              <DialogTitle>{t("tenderBoard.dialog.dismissTitle")}</DialogTitle>
              <DialogDescription>{subject}</DialogDescription>
            </DialogHeader>
            {dialog.rows.some(row => row.process) && (
              <p className="text-sm text-muted-foreground">{t("tenderBoard.dialog.processNote")}</p>
            )}
            <Textarea
              rows={3}
              placeholder={t("tenderBoard.actions.dismissReason")}
              aria-label={t("tenderBoard.actions.dismissReason")}
              value={value}
              onChange={event => setValue(event.target.value)}
            />
            {confirm(t("tenderBoard.actions.dismiss"), true)}
          </>
        )}

        {dialog.kind === "handover" && (
          <>
            <DialogHeader>
              <DialogTitle>{t("tenderBoard.dialog.handoverTitle")}</DialogTitle>
              <DialogDescription>{subject}</DialogDescription>
            </DialogHeader>
            <Dropdown
              placeholder={t("tenderBoard.actions.handoverTo")}
              options={roles}
              selectedKey={value}
              onChange={(_e: any, item: any) => setValue((item?.key as string) ?? "")}
            />
            {confirm(t("tenderBoard.actions.handover"), !!value)}
          </>
        )}

        {dialog.kind === "assign" && (
          <>
            <DialogHeader>
              <DialogTitle>{t("tenderBoard.dialog.assignTitle")}</DialogTitle>
              <DialogDescription>{subject}</DialogDescription>
            </DialogHeader>
            <Dropdown
              placeholder={t("tenderBoard.dialog.assignPlaceholder")}
              options={people}
              selectedKey={value}
              onChange={(_e: any, item: any) => setValue((item?.key as string) ?? "")}
            />
            {confirm(t("tenderBoard.dialog.assign"), !!value)}
          </>
        )}

        {dialog.kind === "startProcess" && (
          <>
            <DialogHeader>
              <DialogTitle>{t("tenderBoard.dialog.startTitle")}</DialogTitle>
              <DialogDescription>{subject}</DialogDescription>
            </DialogHeader>
            {definitions.length > 1 ? (
              <Dropdown
                placeholder={t("tenderBoard.actions.startProcessOn")}
                options={definitions}
                selectedKey={value}
                onChange={(_e: any, item: any) => setValue((item?.key as string) ?? "")}
              />
            ) : (
              // Say WHAT starts — a preselection the user never sees starts something unannounced.
              <p className="text-sm">{t("tenderBoard.dialog.startWith", { workflow: definitions[0]?.text ?? "—" })}</p>
            )}
            {confirm(t("tenderBoard.actions.startProcess"), !!value)}
          </>
        )}

        {dialog.kind === "existingCustomer" && <ExistingCustomerResults rows={dialog.rows} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

/** The Bestandskunden-Prüfung: the buyer of each row against the own CRM, fuzzy, with scores. */
function ExistingCustomerResults({ rows, onClose }: { rows: TenderBoardRow[]; onClose: () => void }) {
  const { t } = useTranslation();
  const api = useTenderSearchProfileAPI();
  const [results, setResults] = useState<Record<string, TenderExistingCustomerCheck | { error: string }>>({});

  useEffect(() => {
    let cancelled = false;
    setResults({});
    rows.forEach(row => {
      api
        .checkExistingCustomer(row.tenderId)
        .then(result => !cancelled && setResults(previous => ({ ...previous, [row.tenderId]: result })))
        .catch((err: any) =>
          !cancelled &&
          setResults(previous => ({ ...previous, [row.tenderId]: { error: err?.message ?? String(err) } }))
        );
    });
    return () => {
      cancelled = true;
    };
    // `api` is rebuilt on every render by its hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t("tenderBoard.dialog.existingCustomerTitle")}</DialogTitle>
        <DialogDescription>{t("tenderBoard.dialog.existingCustomerDescription")}</DialogDescription>
      </DialogHeader>
      <ul className="max-h-[60vh] space-y-3 overflow-y-auto">
        {rows.map(row => {
          const result = results[row.tenderId];
          return (
            <li key={row.tenderId} className="rounded-md border border-border p-3 text-sm">
              <div className="font-medium">{row.buyer ?? "—"}</div>
              <div className="text-xs text-muted-foreground">{row.title}</div>
              <div className="mt-2">
                {!result ? (
                  <span className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 size={12} className="animate-spin" /> {t("tenderBoard.loading")}
                  </span>
                ) : "error" in result ? (
                  <span className="text-xs text-red-700">{result.error}</span>
                ) : !result.crm ? (
                  <span className="text-xs text-muted-foreground">{t("tenderBoard.dialog.noCrm")}</span>
                ) : result.candidates.length === 0 ? (
                  <span className="text-xs text-muted-foreground">{t("tenderBoard.dialog.noMatch")}</span>
                ) : (
                  <ul className="space-y-1">
                    {result.candidates.map(candidate => (
                      <li key={candidate.name} className="flex items-center gap-2 text-xs">
                        <Building2 size={12} className="shrink-0 text-muted-foreground" />
                        <span className="flex-1">{candidate.name}</span>
                        <span className="tabular-nums text-muted-foreground">
                          {Math.round(candidate.score * 100)} %
                        </span>
                        {candidate.exact && (
                          <span className="rounded bg-foreground px-1.5 py-px text-[10px] text-background">
                            {t("tenderBoard.existingCustomer.badge")}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          {t("global.close")}
        </Button>
      </DialogFooter>
    </>
  );
}
